import { Component, inject, OnInit, Signal } from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { SharedModule } from '../../../shared/shared.module';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  BookingsScope,
  BookingsState,
} from '../../../core/store/bookings/bookings.state';
import { endOfDay, format, startOfDay } from 'date-fns';
import { ApiError } from '../../../core/models/common/api-error';
import { Store } from '@ngrx/store';
import { selectBookingsState } from '../../../core/store/reducers';
import { BookingsService } from '../../../core/services/bookings.service';
import { ToastService } from '../../../core/services/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';

@Component({
  selector: 'app-export-excel-modal',
  imports: [SharedModule],
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="submit()">
      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <app-date-input [control]="form.controls.from" label="A partir de" />
        <app-date-input [control]="form.controls.until" label="Jusqu'à" />
      </div>

      <div class="space-y-2">
        @for (property of propertiesForm.controls; track property.value.name) {
          <div class="flex items-center gap-4">
            <app-single-checkbox-input
              [name]="property.value.name + '_' + 'active'"
              [control]="property.controls.active"
              [marginBottom]="false"
            />

            <app-text-input
              class="grow"
              [control]="property.controls.label"
              [name]="property.value.name + '_' + 'label'"
              [marginBottom]="false"
              [marginTop]="false"
            />
          </div>
        }
      </div>
    </app-modal>
  `,
})
export class ExportExcelModalComponent
  extends ModalBaseComponent
  implements OnInit
{
  store = inject(Store);
  bookingsService = inject(BookingsService);
  toastService = inject(ToastService);
  modalService = inject(ModalService);
  bookingsState: Signal<BookingsState> =
    this.store.selectSignal(selectBookingsState);
  form = new FormGroup({
    from: new FormControl(null),
    until: new FormControl(null),
  });

  bookingProperties = [
    { name: 'reference', label: 'N° réservation', active: true },
    { name: 'start', label: 'Date événement', active: true },
    { name: 'end', label: 'Fin événement', active: true },
    {
      name: 'created_at',
      label: 'Réservation créée le',
      active: true,
    },
    { name: 'room.id', label: 'N° salle', active: true },
    { name: 'room.name', label: 'Salle', active: true },
    {
      name: 'status',
      label: 'Statut réservation',
      active: true,
    },
    { name: 'name', label: 'Titre', active: true },
    { name: 'full_name', label: 'Nom', active: true },
    { name: 'company', label: 'Organisation', active: true },
    { name: 'full_address', label: 'Rue et n°', active: true },
    { name: 'email', label: 'E-mail', active: true },
    { name: 'phone', label: 'N° de téléphone', active: true },
    {
      name: 'eventType.name',
      label: "Type d'événement",
      active: true,
    },
    { name: 'comment', label: 'Commentaire', active: true },
    {
      name: 'rate.name',
      label: 'Groupe de tarif',
      active: true,
    },
    {
      name: 'attendance',
      label: 'Nombre de personnes',
      active: true,
    },
    {
      name: 'gross_total',
      label: 'Montant brut',
      active: true,
    },
    {
      name: 'first_invoice_coupon_code',
      label: 'Code promo',
      active: true,
    },
    {
      name: 'first_invoice_coupon_discount',
      label: 'Valeur code promo',
      active: true,
    },
    {
      name: 'invoices_total',
      label: 'Montant net',
      active: true,
    },
    { name: 'balance', label: 'Solde', active: true },
    {
      name: 'invoicing_status',
      label: 'Statut facture',
      active: true,
    },
  ];

  propertiesForm = new FormArray<
    FormGroup<{
      name: FormControl<string>;
      label: FormControl<string>;
      active: FormControl<boolean>;
    }>
  >([]);

  ngOnInit() {
    this.modalInstance.config.update(config => ({
      ...config,
      title: 'Exportation des réservations',
      okAction: {
        ...config.okAction,
        title: 'Exporter'
      }
    }));

    this.form.patchValue({
      from: this.bookingsState()[BookingsScope.All].from
        ? [new Date(this.bookingsState()[BookingsScope.All].from)]
        : null,
      until: this.bookingsState()[BookingsScope.All].to
        ? [new Date(this.bookingsState()[BookingsScope.All].to)]
        : null,
    });

    const ebp = JSON.parse(localStorage.getItem('exportBookingsProperties'));
    for (const property of this.bookingProperties) {
      const savedProperty = (ebp ?? []).find((el) => el.name === property.name);
      this.propertiesForm.push(
        new FormGroup({
          name: new FormControl(property.name),
          label: new FormControl(
            savedProperty?.label?.length
              ? savedProperty?.label
              : property.label,
          ),
          active: new FormControl(savedProperty?.active ?? property.active),
        }),
      );
    }
  }

  async submit() {
    try {
      localStorage.setItem(
        'exportBookingsProperties',
        JSON.stringify(this.propertiesForm.value),
      );
    } catch (exception) {}

    try {
      this.modalInstance.saving = true;
      const data = await this.bookingsService.exportAsExcel({
        from: this.form.value.from?.length
          ? DatetimeHelper.dateToApiFormat(
              startOfDay(this.form.value.from[0]),
              true,
            )
          : null,
        to: this.form.value.until?.length
          ? DatetimeHelper.dateToApiFormat(
              endOfDay(this.form.value.until[0]),
              true,
            )
          : null,
        order_by: this.bookingsState()[BookingsScope.All].order?.map(
          (orderBy) => [orderBy.field, orderBy.order],
        ) as any,
        room_ids: this.bookingsState()[BookingsScope.All].roomsFilter.ids ?? [],
        attributes: this.propertiesForm.value
          .filter((el) => el.active)
          .map((el) => ({ name: el.name, label: el.label })),
      });
      const blob = new Blob([data], { type: 'application/ms-excel' });
      const fileName = `Export-Location-Salles-Reservations-${format(
        new Date(),
        'yyyy-MM-dd',
      )}.xlsx`;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      this.modalService.close();
    } catch (exception) {
      console.error(exception);
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.modalInstance.saving = false;
    }
  }
}
